import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API_BASE } from "@/App";
import { useTrackSection } from "@/hooks/useTrackSection";
import { 
  Brain, Search, ArrowLeft, Clock, BookOpen, Target, 
  Loader2, History, Lightbulb, CheckCircle, XCircle,
  FileText, AlertTriangle, Flame, Sparkles, ChevronRight,
  GraduationCap, Zap, BookMarked, Lock
} from "lucide-react";
import ReactMarkdown from "react-markdown";

// Markdown components defined outside render to avoid React warnings
const markdownComponents = {
  h1: ({children}) => <h1 className="text-xl font-bold text-slate-800 mt-6 mb-3">{children}</h1>,
  h2: ({children}) => <h2 className="text-lg font-bold text-slate-800 mt-5 mb-2">{children}</h2>,
  h3: ({children}) => <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">{children}</h3>,
  p: ({children}) => {
    const text = String(children);
    if (text.includes("UPGRADE TO PRO") || text.includes("Unlock full exam-oriented")) {
      return null;
    }
    return <p className="text-slate-600 mb-3 leading-relaxed">{children}</p>;
  },
  ul: ({children}) => <ul className="list-disc pl-5 space-y-1 text-slate-600 mb-4">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal pl-5 space-y-1 text-slate-600 mb-4">{children}</ol>,
  li: ({children}) => <li className="text-slate-600">{children}</li>,
  strong: ({children}) => {
    const text = String(children);
    if (text.includes("UPGRADE TO PRO")) {
      return null;
    }
    return <strong className="font-bold text-slate-800">{children}</strong>;
  },
  blockquote: ({children}) => <blockquote className="border-l-4 border-purple-500 pl-4 italic text-slate-600 my-4">{children}</blockquote>,
  hr: () => null,
};

/**
 * CLARIAS Concept Intelligence - SuperKalam Inspired Vibrant Design
 * UPSC-aligned concept explainer
 */
const ConceptIntelligencePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useTrackSection(); // Track section visit
  const [concept, setConcept] = useState("");
  const [context, setContext] = useState("");
  const [selectedModel, setSelectedModel] = useState("gpt-4o");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const abortControllerRef = React.useRef(null);

  const isPro = user?.role === "PRO";
  const firstName = user?.name?.split(" ")[0] || "Aspirant";

  const MODELS = [
    { id: "gpt-4o", name: "GPT-4o", provider: "OpenAI", color: "bg-emerald-500", primary: true },
    { id: "claude-sonnet-4", name: "Claude Sonnet 4", provider: "Anthropic", color: "bg-orange-500", primary: false },
  ];

  useEffect(() => {
    setLoading(false);
    setError(null);
    
    fetchSuggestions();
    fetchHistory();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/concept/suggested`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/concept/history?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.history || []);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  };

  const handleExplain = async (conceptToExplain = null) => {
    const searchConcept = conceptToExplain || concept;
    if (!searchConcept.trim()) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/concept/explain`,
        { concept: searchConcept, context: context || null },
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortControllerRef.current.signal
        }
      );
      setResult(response.data);
      setConcept(searchConcept);
      fetchHistory();
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.response?.data?.detail || "Failed to explain concept");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Polity": "from-blue-500 to-indigo-500",
      "Economy": "from-emerald-500 to-teal-500",
      "Geography": "from-amber-500 to-orange-500",
      "History": "from-purple-500 to-pink-500",
      "Environment": "from-green-500 to-emerald-500",
      "IR": "from-rose-500 to-red-500"
    };
    return colors[category] || "from-slate-500 to-gray-500";
  };

  const getCategoryBg = (category) => {
    const colors = {
      "Polity": "bg-blue-100 border-blue-200",
      "Economy": "bg-emerald-100 border-emerald-200",
      "Geography": "bg-amber-100 border-amber-200",
      "History": "bg-purple-100 border-purple-200",
      "Environment": "bg-green-100 border-green-200",
      "IR": "bg-rose-100 border-rose-200"
    };
    return colors[category] || "bg-slate-100 border-slate-200";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-purple-50/30">
      
      {/* ═══════════════════════════════════════════════════════════
          VIBRANT HEADER
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Top Row - Back & Title */}
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Concept Intelligence</h1>
                <p className="text-sm text-purple-200">UPSC-Aligned Concept Explainer</p>
              </div>
            </div>
            {isPro && (
              <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 font-semibold px-3 py-1">
                <Flame className="w-3 h-3 mr-1" />
                PRO
              </Badge>
            )}
          </div>

          {/* Search Box */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
                <Input
                  placeholder="Enter a UPSC concept (e.g., Article 370, Fiscal Deficit)"
                  value={concept}
                  onChange={(e) => setConcept(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleExplain()}
                  className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12 rounded-xl text-base"
                />
              </div>
              <Button
                onClick={() => handleExplain()}
                disabled={loading || !concept.trim()}
                className="bg-white text-purple-600 hover:bg-white/90 h-12 px-6 rounded-xl font-semibold"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Explain
                  </>
                )}
              </Button>
            </div>
            
            {/* Context Input */}
            <Input
              placeholder="Optional: Add context (e.g., 'for Prelims', 'GS2 perspective')"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-xl"
            />

            {/* Access Badge */}
            <div className="flex items-center gap-2">
              {!isPro ? (
                <span className="px-3 py-1 rounded-full text-xs bg-amber-500/20 text-amber-200 font-medium">
                  FREE Access
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-200 font-medium">
                  PRO Access - Full Explanations
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        
        {/* ═══════════════════════════════════════════════════════════
            ERROR STATE
            ═══════════════════════════════════════════════════════════ */}
        {error && (
          <Card className="border-red-200 bg-gradient-to-r from-red-50 to-orange-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            RESULT CARD
            ═══════════════════════════════════════════════════════════ */}
        {result && (
          <Card className="border-0 shadow-xl overflow-hidden">
            {/* Result Header */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{result.concept}</h2>
                  <p className="text-sm text-purple-200">Concept Explanation</p>
                </div>
              </div>
            </div>
            
            <CardContent className="p-5">
              <div className="prose prose-slate max-w-none">
                <ReactMarkdown components={markdownComponents}>
                  {result.explanation}
                </ReactMarkdown>
              </div>
              
              {/* Upgrade CTA for FREE users */}
              {result.user_type === "FREE" && (
                <div 
                  className="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate("/upgrade")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-purple-900">Upgrade to PRO</p>
                      <p className="text-sm text-purple-700">Unlock full exam-oriented explanation & more</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
              )}
              
              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Brain className="w-3 h-3" />
                  CLARIAS Concept Intelligence
                </span>
                <span>{new Date(result.generated_at).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            SUGGESTIONS (when no result)
            ═══════════════════════════════════════════════════════════ */}
        {!result && !loading && (
          <>
            {/* History Toggle */}
            {history.length > 0 && (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 font-medium"
                >
                  <History className="w-4 h-4" />
                  {showHistory ? "Hide History" : "Show Recent Searches"}
                </button>
              </div>
            )}

            {/* History Cards */}
            {showHistory && history.length > 0 && (
              <Card className="border-0 shadow-lg">
                <div className="bg-gradient-to-r from-slate-100 to-violet-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-violet-500" />
                    Recent Searches
                  </h3>
                </div>
                <CardContent className="p-3">
                  <div className="space-y-2">
                    {history.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleExplain(item.concept)}
                        className="w-full text-left p-3 rounded-xl hover:bg-violet-50 transition-colors flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                            <Brain className="w-4 h-4 text-violet-600" />
                          </div>
                          <span className="text-slate-700 font-medium">{item.concept}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-violet-500 transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Suggested Concepts */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-slate-800">Suggested Concepts</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {suggestions.map((item, idx) => (
                  <Card
                    key={idx}
                    className={`border-2 cursor-pointer hover:shadow-lg transition-all group overflow-hidden ${getCategoryBg(item.category)}`}
                    onClick={() => handleExplain(item.concept)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${getCategoryColor(item.category)} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{item.concept}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          <Badge variant="outline" className="text-xs">{item.paper}</Badge>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors flex-shrink-0" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* How it works */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-violet-50 to-purple-50 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-4 py-3">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  How CLARIAS Explains Concepts
                </h3>
              </div>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  {[
                    { icon: CheckCircle, label: "One-line Definition", color: "text-emerald-600 bg-emerald-100" },
                    { icon: FileText, label: "GS Paper Mapping", color: "text-blue-600 bg-blue-100" },
                    { icon: Target, label: "Prelims & Mains Angle", color: "text-purple-600 bg-purple-100" },
                    { icon: Clock, label: "Time Guidance", color: "text-amber-600 bg-amber-100" },
                    { icon: XCircle, label: "Stop Signal", color: "text-red-600 bg-red-100" },
                  ].map((item, idx) => (
                    <div key={idx} className="text-center p-3">
                      <div className={`w-10 h-10 ${item.color} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <p className="text-xs text-slate-600 font-medium">{item.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════
            LOADING STATE
            ═══════════════════════════════════════════════════════════ */}
        {loading && (
          <Card className="border-0 shadow-xl">
            <CardContent className="p-10 text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto" />
                <Brain className="w-6 h-6 text-purple-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="mt-4 text-slate-700 font-medium">Analyzing concept for UPSC relevance...</p>
              <p className="text-sm text-slate-500 mt-2">Mapping to GS papers, extracting keywords...</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ConceptIntelligencePage;
